import { requireAccountDatabase, type DBUserIdentity } from '../../db/database';

export type IdentityCacheCommit = {
  rows: DBUserIdentity[];
  ownerUserId: number;
  nextChangeCursor?: number;
};

/**
 * The only persistence boundary for Web public identity data.
 *
 * Callers submit an already validated page. Rows and the optional IM cursor are
 * committed in one Dexie transaction so a crash can never persist a cursor
 * that skips identities which were not durably cached.
 */
export class IdentityRepository {
  async loadAll(): Promise<DBUserIdentity[]> {
    return requireAccountDatabase().userIdentityCache.toArray();
  }

  async getLastAppliedCursor(ownerUserId: number): Promise<number> {
    const row = await requireAccountDatabase().identitySyncState.get(ownerUserId);
    return row?.lastAppliedChangeCursor ?? 0;
  }

  async commit(command: IdentityCacheCommit): Promise<DBUserIdentity[]> {
    const db = requireAccountDatabase();
    const accepted: DBUserIdentity[] = [];

    await db.transaction(
      'rw',
      db.userIdentityCache,
      db.identitySyncState,
      async () => {
        for (const incoming of command.rows) {
          const current = await db.userIdentityCache.get(incoming.subjectUserId);
          if (current && current.profileVersion > incoming.profileVersion) {
            continue;
          }
          if (current && current.profileVersion === incoming.profileVersion) {
            const same =
              current.availability === incoming.availability &&
              current.displayName === incoming.displayName &&
              current.avatarAssetId === incoming.avatarAssetId;
            if (!same) {
              throw new Error(
                `Identity contract violation at equal profileVersion for user ${incoming.subjectUserId}`,
              );
            }
            // The durable row is already current, but the network response may
            // carry a freshly resolved media URL which is intentionally not
            // persisted. Return it so the Store can attach that projection.
            accepted.push(incoming);
            continue;
          }
          await db.userIdentityCache.put(incoming);
          accepted.push(incoming);
        }

        if (command.nextChangeCursor != null) {
          const currentState = await db.identitySyncState.get(command.ownerUserId);
          if ((currentState?.lastAppliedChangeCursor ?? 0) > command.nextChangeCursor) {
            throw new Error('Identity cursor cannot move backwards');
          }
          await db.identitySyncState.put({
            ownerUserId: command.ownerUserId,
            lastAppliedChangeCursor: command.nextChangeCursor,
            updatedAtMs: Date.now(),
          });
        }
      },
    );

    return accepted;
  }
}
